import api from '@api/apiServices';
import AutoColorAvatar from '@Components/General/AutoColorAvatar';
import { ContactField } from '@Components/General/DynamicTable/Cells';
import useSidePan from '@hooks/useSidePan';
import { AddRounded } from '@mui/icons-material';
import {
    Autocomplete,
    Avatar,
    Box,
    Card,
    CardContent,
    Chip,
    createFilterOptions,
    FormControl,
    FormHelperText,
    InputLabel,
    ListItemIcon,
    MenuItem,
    Select,
    Stack,
    TextField,
} from '@mui/material';
import { useInfiniteQuery, useSuspenseQuery } from '@tanstack/react-query';
import { useDebounce } from '@uidotdev/usehooks';
import { fromEmailsQueryOptions } from '@utils/queryOptions';
import { setValueAs } from '@utils/utils';
import GroupAvatar from '@Views/Contacts/groups/Components/GroupAvatar';
import { t } from 'i18next';
import { useMemo, useState } from 'react';
import { Controller, UseFormReturn } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import { InView } from 'react-intersection-observer';

const recipientsLimit = 50;

const groupTitleMap = {
    contact: t('Contacts'),
    contactgroup: t('Groups'),
};

const ContactChip = ({ data: contact, ...props }) => {
    const openSidePan = useSidePan();
    return (
        <Chip
            label={contact.display_name}
            avatar={
                <AutoColorAvatar
                    src={contact.profile_picture}
                    value={contact.display_name}
                />
            }
            onClick={() => openSidePan({ contactId: contact.contact_id })}
            {...props}
        />
    );
};

const GroupChip = ({ data: group, ...props }) => {
    return (
        <Chip
            label={group.name + ` (${group.count})`}
            avatar={<GroupAvatar group={group} />}
            {...props}
        />
    );
};

const PlainEmailChip = ({ data, ...props }) => {
    return <Chip label={data.email} avatar={<Avatar />} {...props} />;
};

const TagMap = {
    contact: ContactChip,
    contactgroup: GroupChip,
    plainemail: PlainEmailChip,
};

const GroupOption = ({ option }) => {
    const { data: group } = option;

    const description = `${group.count} ${t('Contacts')}${group.description ? ' • ' + group.description : ''}`;
    return (
        <ContactField
            row={{ ...group, description }}
            primaryField="name"
            avatar={<GroupAvatar group={group} />}
        />
    );
};

const ContactOption = ({ option }) => {
    const { data } = option;

    return (
        <ContactField row={data} secondaryField="email_addresses[*].email" />
    );
};

const optionComponents = {
    contact: ContactOption,
    contactgroup: GroupOption,
};

const getOptionLabel = option => {
    if (typeof option === 'string') return option;
    const { data } = option;
    switch (option.type) {
        case 'contactgroup':
            return `${data.name} (${data.count}) - ${
                data.email_count
            } ${t('Emails')}`;
        case 'contact':
            const emailCount = data.email_addresses?.length;
            const displayEmail = emailCount
                ? `<${data.email_addresses[0]}>`
                : `(No Email)`;
            const plusEmails =
                emailCount > 1 && emailCount - 1 ? ` +${emailCount - 1}` : '';

            return `${data.display_name} ${displayEmail}${plusEmails}`;
        case 'plainemail':
            return data.email;
        default:
            return option.type;
    }
};

const DraftForm = ({
    draft,
    formHook,
}: {
    draft: any;
    formHook: UseFormReturn<any>;
}) => {
    const { t } = useTranslation();
    const { control, register } = formHook;
    const [recipientsSearch, setRecipientsSearch] = useState('');
    const debouncedSearch = useDebounce(recipientsSearch, 400);

    const { data: fromEmails } = useSuspenseQuery(fromEmailsQueryOptions());

    const seriaizeRecipientChange = value => {
        const recipients = structuredClone(value);
        const newRecipient = recipients.pop();

        if (typeof newRecipient === 'string') {
            const flatPlainEmails = recipients
                .filter(recipient => recipient.type === 'plainemail')
                .flatMap(recipient => recipient.data.email);
            const emails = newRecipient
                .extractEmails()
                .filter(email => !flatPlainEmails.includes(email));

            const plainRecipients = emails.map(email => ({
                type: 'plainemail',
                data: { email },
            }));
            return recipients.concat(plainRecipients);
        }

        return value;
    };

    const fetchRecipients = ({ pageParam }) => {
        const params = {
            limit: recipientsLimit,
            offset: pageParam,
            search: debouncedSearch,
        };

        return api.emails['recipients-options'].get(params).then(res => {
            const { results: options, count } = res;
            const nextOffset =
                count > pageParam + recipientsLimit
                    ? pageParam + recipientsLimit
                    : undefined;
            return { options, nextOffset };
        });
    };

    const recipientsQuery = useInfiniteQuery({
        queryKey: ['recipients-options', debouncedSearch],
        queryFn: fetchRecipients,
        initialPageParam: 0,
        getNextPageParam: (lastPage, pages) => lastPage.nextOffset,
    });

    const {
        data: recipientsData,
        isLoading: recipientsLoading,
        fetchNextPage,
    } = recipientsQuery;

    const recipientsOptions = useMemo(
        () => recipientsData?.pages?.flatMap(page => page.options),
        [recipientsData],
    );

    return (
        <Card sx={{ flex: 1 }}>
            <CardContent sx={{ m: 4 }}>
                <Stack spacing={2}>
                    <Stack direction="row" spacing={2}>
                        <TextField
                            fullWidth
                            label={t('Subject')}
                            variant="filled"
                            {...register('subject')}
                        />
                        <Controller
                            name="from_email"
                            control={control}
                            render={({
                                field: { onChange, ...restField },
                                fieldState: { error },
                            }) => (
                                <FormControl
                                    variant="filled"
                                    sx={{ width: 360 }}
                                    error={Boolean(error)}
                                >
                                    <InputLabel>{t('From Email')}</InputLabel>
                                    <Select
                                        fullWidth
                                        variant="filled"
                                        {...restField}
                                        onChange={e => {
                                            if (
                                                e.target.value === 'new_email'
                                            ) {
                                                window.open(
                                                    '/settings/emails',
                                                    '_blank',
                                                );
                                            } else {
                                                onChange(e);
                                            }
                                        }}
                                    >
                                        {fromEmails.map(email => (
                                            <MenuItem value={email} key={email}>
                                                {email}
                                            </MenuItem>
                                        ))}
                                        <MenuItem
                                            value="new_email"
                                            key="new_email"
                                            sx={{
                                                color: 'primary.main',
                                            }}
                                        >
                                            <ListItemIcon>
                                                <AddRounded />
                                            </ListItemIcon>
                                            {t('Add Email')}
                                        </MenuItem>
                                    </Select>
                                    <FormHelperText>
                                        {error?.message}
                                    </FormHelperText>
                                </FormControl>
                            )}
                        />
                    </Stack>
                    <TextField
                        fullWidth
                        label={t('Preview Text')}
                        variant="filled"
                        helperText={t(
                            'Appears in the inbox after the subject line',
                        )}
                        {...register('preview_text', { setValueAs })}
                    />
                    <TextField
                        multiline
                        variant="filled"
                        label={t('Description')}
                        placeholder={t('What is this email about') + '?'}
                        maxRows={2}
                        {...register('description', { setValueAs })}
                        helperText={t('Only visible to you')}
                    />
                    <Controller
                        name="recipients"
                        control={control}
                        render={({
                            field: { onChange, ...rest },
                            fieldState: { error },
                        }) => (
                            <Autocomplete
                                multiple
                                freeSolo
                                // clearOnBlur
                                disableClearable
                                disableCloseOnSelect
                                isOptionEqualToValue={(option, value) => {
                                    if (!value?.data?.id) return false;
                                    return option.data?.id === value.data?.id;
                                }}
                                options={recipientsOptions || []}
                                renderTags={(
                                    value,
                                    getTagProps,
                                    ownerState,
                                ) => {
                                    return value.map((option, index) => {
                                        const { key, ...tagProps } =
                                            getTagProps({ index });

                                        const TagComponent =
                                            TagMap[option.type];

                                        return (
                                            <TagComponent
                                                key={key}
                                                data={option.data}
                                                sx={{
                                                    '& .MuiChip-avatar': {
                                                        color: 'white',
                                                    },
                                                }}
                                                {...tagProps}
                                            />
                                        );
                                    });
                                }}
                                filterOptions={createFilterOptions({
                                    stringify: op =>
                                        op.data.display_name +
                                        op.data.email_addresses +
                                        op.data.contact_id +
                                        op.data.name,
                                })}
                                {...rest}
                                onChange={(_, value) =>
                                    onChange(seriaizeRecipientChange(value))
                                }
                                onInputChange={(_, value) =>
                                    setRecipientsSearch(value)
                                }
                                groupBy={option => groupTitleMap[option.type]}
                                getOptionLabel={getOptionLabel}
                                renderOption={(
                                    { key, ...optionProps },
                                    option,
                                ) => {
                                    const Component =
                                        optionComponents[option.type];
                                    const showInView =
                                        option ===
                                        recipientsOptions?.at(-recipientsLimit);

                                    if (showInView)
                                        return (
                                            <InView
                                                key={option.id}
                                                onChange={isInView =>
                                                    isInView && fetchNextPage()
                                                }
                                                {...optionProps}
                                            >
                                                <Component option={option} />
                                            </InView>
                                        );
                                    return (
                                        <Box key={option.id} {...optionProps}>
                                            <Component option={option} />
                                        </Box>
                                    );
                                }}
                                loading={recipientsLoading}
                                renderInput={params => (
                                    <TextField
                                        {...params}
                                        variant="filled"
                                        label={t('Recipients')}
                                        error={Boolean(error)}
                                        helperText={error?.message}
                                    />
                                )}
                                onBlur={e => {
                                    if (e.target.value) {
                                        onChange(
                                            seriaizeRecipientChange([
                                                e.target.value,
                                                ...rest.value,
                                            ]),
                                        );
                                    }
                                }}
                            />
                        )}
                    />
                </Stack>
            </CardContent>
            {/* <ContactSidePan /> */}
        </Card>
    );
};

export default DraftForm;
